/**
 * Aspect — Picker (selection via ray vs world-AABB)
 * --------------------------------------------------------------
 * Casts a ray from the camera through the pointer and selects the nearest
 * furniture instance whose world AABB it intersects. Selection is non-
 * destructive: a mesh-conforming outline is cloned around the selected item
 * rather than mutating its (shared) materials.
 *
 * Emits the selection as an app event and notifies the server (viewport:selected)
 * without touching the DOM.
 */

import * as pc from "playcanvas";
import {
  attachSelectionOutline,
  createOutlineMaterial,
  detachSelectionOutline,
  hasSelectionOutline,
  pulseOutlineMaterial,
} from "./selection_outline.js";

/** Union the world-space AABBs of an entity's (and descendants') mesh instances. */
function worldAABB(entity) {
  const aabb = new pc.BoundingBox();
  let initialized = false;
  const renders = entity.findComponents("render");
  for (const render of renders) {
    for (const mi of render.meshInstances ?? []) {
      if (!mi?.aabb) continue;
      if (!initialized) {
        aabb.copy(mi.aabb);
        initialized = true;
      } else {
        aabb.add(mi.aabb);
      }
    }
  }
  return initialized ? aabb : null;
}

export class Picker {
  /**
   * @param {pc.AppBase} app
   * @param {pc.Entity} camera
   * @param {{ entities: Map<string, pc.Entity> }} sceneManager
   * @param {import('../network.js').NetworkManager} [net]
   * @param {import('./door_system.js').DoorSystem} [doors]
   */
  constructor(app, camera, sceneManager, net = null, doors = null, lights = null, placement = null) {
    this.app = app;
    this.camera = camera;
    this.scene = sceneManager;
    this.net = net;
    this.doors = doors;
    this.lights = lights;
    this.placement = placement;
    this.selectedId = null;
    /** Multi-selection (marquee) — ids move as a group in GizmoControls. */
    this.selectedIds = new Set();
    this._outlineEntity = null;
    this._multiOutlined = [];
    this._outlineMat = createOutlineMaterial();
    this._glowT = 0;
    // Read-only tour/embed clients can look and toggle doors but never select
    // furniture (selection is the entry point to gizmo editing / removal).
    this.readOnly = false;
    // Phones use walk/look/gyro — furniture selection fights multitouch controls.
    this.disableSelect =
      (typeof window !== "undefined" &&
        (window.matchMedia?.("(pointer: coarse)")?.matches || (navigator.maxTouchPoints ?? 0) > 0)) ||
      false;

    // In overhead scene-view, left-drag is owned by structure selection, so the
    // furniture picker must not also fire on left-click there.
    this._overhead = false;
    this.app.on("cam:mode-changed", ({ mode }) => {
      this._overhead = mode === "overhead";
    });

    this.app.on("update", (dt) => {
      if (!this._outlineEntity) return;
      this._glowT += dt ?? 0.016;
      pulseOutlineMaterial(this._outlineMat, this._glowT);
    });

    this._bindInput();
  }

  _screenPickCoords(x, y) {
    const canvas = this.app.graphicsDevice?.canvas;
    if (canvas && document.pointerLockElement === canvas) {
      return { x: canvas.clientWidth / 2, y: canvas.clientHeight / 2 };
    }
    return { x, y };
  }

  _isPointerLocked() {
    const canvas = this.app.graphicsDevice?.canvas;
    return Boolean(canvas && document.pointerLockElement === canvas);
  }

  _clearOutline() {
    if (this._outlineEntity) {
      detachSelectionOutline(this._outlineEntity);
      this._outlineEntity = null;
    }
    for (const ent of this._multiOutlined) {
      try {
        detachSelectionOutline(ent);
      } catch {
        /* entity may be gone */
      }
    }
    this._multiOutlined = [];
  }

  /** Clear the current selection. */
  deselect() {
    this.select(null);
  }

  /** Raycast furniture at a screen point. Returns { id, t } for the nearest hit. */
  _raycastFurniture(x, y) {
    const { origin, dir } = this._screenRay(x, y);
    let bestId = null;
    let bestT = Infinity;

    for (const [id, entity] of this.scene.entities) {
      if (!entity.enabled) continue;
      const aabb = worldAABB(entity);
      if (!aabb) continue;
      const t = this._rayAABB(origin, dir, aabb);
      if (t !== null && t < bestT) {
        bestT = t;
        bestId = id;
      }
    }
    return bestId !== null ? { id: bestId, t: bestT } : null;
  }

  _bindInput() {
    if (this.app.mouse) {
      this.app.mouse.on(pc.EVENT_MOUSEDOWN, (e) => {
        if (e.button === pc.MOUSEBUTTON_RIGHT) {
          if (this.placement?.active) {
            this.app.fire("ui:cancel-placement", {});
            return;
          }
          if (this.selectedIds.size) {
            this.app.fire("ui:edit-cancel-multi", {});
            this.deselect();
            return;
          }
          if (this.selectedId) {
            this.app.fire("ui:edit-cancel", { id: this.selectedId });
            this.deselect();
          } else if (this._isPointerLocked()) {
            // Nothing selected: right-click releases walk mode, same as Esc.
            this.app.fire("fp:release-lock", {});
          }
          return;
        }
        if (e.button !== pc.MOUSEBUTTON_LEFT) return;
        if (this._overhead) return;
        if (this.placement?.active) return;
        if (this.measure?.active) return; // tape-measure clicks never select
        // Group selection active: left-click drops the group where it stands.
        if (this.selectedIds.size) {
          this.app.fire("ui:commit-multi", {});
          this.deselect();
          return;
        }
        // Unlocked canvas click only re-engages pointer lock — do not select/toggle.
        if (!this._isPointerLocked()) return;

        const { x, y } = this._screenPickCoords(e.x, e.y);
        const doorHit = this.doors?.raycastDoor(x, y);
        const switchHit = this.lights?.raycastSwitch(x, y);
        const furnHit = this._raycastFurniture(x, y);
        // A light switch under the cursor (and nearest) toggles its room's lights.
        if (switchHit && (!doorHit || switchHit.t < doorHit.t) && (!furnHit || switchHit.t < furnHit.t)) {
          this.lights.toggleSwitch(switchHit.id);
          return;
        }
        if (doorHit && (!furnHit || doorHit.t < furnHit.t)) {
          this.doors.toggleDoor(doorHit.id);
          return;
        }
        if (this.selectedId) {
          this.deselect();
          return;
        }
        if (this.disableSelect) return;
        this.select(furnHit?.id ?? null);
      });
    }
    if (this.app.touch) {
      this.app.touch.on(pc.EVENT_TOUCHSTART, (e) => {
        // Mobile: never select furniture — left/right sticks + gyro own the screen.
        if (this.disableSelect || this.readOnly) return;
        if (this._overhead || this.placement?.active || this.measure?.active) return;
        const t = e.touches[0];
        if (t) this.pickAtScreen(t.x, t.y);
      });
    }
    this.app.keyboard?.on(pc.EVENT_KEYDOWN, (e) => this._onKeyDown(e));
  }

  _typingInField() {
    const el = document.activeElement;
    if (!el) return false;
    const tag = el.tagName?.toLowerCase();
    return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
  }

  _onKeyDown(e) {
    if (e.key !== pc.KEY_DELETE && e.key !== pc.KEY_BACKSPACE) return;
    if (this._typingInField()) return;
    if (this.placement?.active) return;
    if (!this.selectedId) return;
    e.event?.preventDefault?.();
    this.removeSelected();
  }

  /** Delete the selected placement from the scene and sync to the server. */
  removeSelected() {
    const id = this.selectedId;
    if (!id) return false;
    this.deselect();
    return this.scene.removePlacement(id);
  }

  /** Build a camera ray through screen coords (origin + normalized direction). */
  _screenRay(x, y) {
    const cam = this.camera.camera;
    const near = cam.screenToWorld(x, y, cam.nearClip);
    const far = cam.screenToWorld(x, y, cam.farClip);
    const dir = far.clone().sub(near).normalize();
    return { origin: near, dir };
  }

  /** Slab-method ray vs AABB; returns entry distance t or null. */
  _rayAABB(origin, dir, aabb) {
    const min = aabb.getMin();
    const max = aabb.getMax();
    let tmin = -Infinity;
    let tmax = Infinity;
    for (const axis of ["x", "y", "z"]) {
      const o = origin[axis];
      const d = dir[axis];
      if (Math.abs(d) < 1e-9) {
        if (o < min[axis] || o > max[axis]) return null;
      } else {
        let t1 = (min[axis] - o) / d;
        let t2 = (max[axis] - o) / d;
        if (t1 > t2) [t1, t2] = [t2, t1];
        tmin = Math.max(tmin, t1);
        tmax = Math.min(tmax, t2);
        if (tmin > tmax) return null;
      }
    }
    return tmax >= 0 ? (tmin >= 0 ? tmin : 0) : null;
  }

  /**
   * Pick the nearest furniture at a screen position. Returns the placement id or
   * null. Also updates selection + outline. Exposed for tests.
   */
  pickAtScreen(x, y) {
    if (this.disableSelect || this.readOnly) return null;
    const hit = this._raycastFurniture(x, y);
    this.select(hit?.id ?? null);
    return hit?.id ?? null;
  }

  select(id) {
    if ((this.readOnly || this.disableSelect) && id != null) return;
    this._clearOutline();
    this.selectedId = id;
    if (this.selectedIds.size) {
      this.selectedIds = new Set();
      this.app.fire("scene:multi-selected", { ids: [] });
    }

    if (!id) {
      this.app.fire("scene:selected", { id: null });
      return;
    }

    this._syncOutline(id);
    const rec = this.scene.placements.get(id);
    const meta = rec?.asset_id ? this.scene.catalog.get(rec.asset_id) : null;
    this.app.fire("scene:selected", { id, name: meta?.name ?? rec?.asset_id ?? id });
    this.net?.notify("viewport:selected", { id });
  }

  /** Marquee multi-selection: outline every id and start group editing. */
  selectMany(ids) {
    if (this.readOnly || this.disableSelect) return;
    const valid = (ids ?? []).filter((id) => this.scene.entities.has(id));
    if (!valid.length) return;
    if (valid.length === 1) {
      this.select(valid[0]);
      return;
    }
    this._clearOutline();
    this.selectedId = null;
    this.selectedIds = new Set(valid);
    for (const id of valid) {
      const ent = this.scene.entities.get(id);
      if (!ent) continue;
      attachSelectionOutline(ent, this._outlineMat);
      this._multiOutlined.push(ent);
    }
    // Fire the single-select event too so walk controls/UI react the same way.
    this.app.fire("scene:selected", { id: valid[0], name: `${valid.length} items` });
    this.app.fire("scene:multi-selected", { ids: valid });
  }

  /** Ensure the mesh outline is attached to the selected entity. */
  _syncOutline(id) {
    const entity = this.scene.entities.get(id);
    if (!entity) {
      this._outlineEntity = null;
      return;
    }

    if (this._outlineEntity !== entity || !hasSelectionOutline(entity)) {
      if (this._outlineEntity) detachSelectionOutline(this._outlineEntity);
      attachSelectionOutline(entity, this._outlineMat);
    }
    this._outlineEntity = entity;
  }

  /** Rebuild the outline if the entity mesh changed (e.g. asset swap). */
  refresh() {
    if (this.selectedId) this._syncOutline(this.selectedId);
  }

  destroy() {
    this._clearOutline();
    this._outlineMat?.destroy();
  }
}

export default Picker;
